<template>
  <panel-layout
    id="courseRun-panel"
    :loading="loading"
    :shouldDismiss="() => !unsavedChanges"
    :didAttemptToDismiss="cancel"
  >
    <template #actions-right>
      <div v-if="courseRun" class="flex items-center space-x-2">
        <o-history-link :item="courseRun" model="CourseRun" icon="history" slim flat />
      </div>
    </template>

    <template #heading>
      <div v-if="courseRun" class="flex items-center space-x-2">
        <div>
          <h2
            class="text-lg md:text-xl tracking-tight font-bold text-gray-800/85 leading-none dark:text-gray-100"
          >
            {{ courseRun.isNewRecord ? $t('pages.course.edit.newRunTitle', { name: courseRun.course.name }) : $t('pages.course.edit.runTitle', { name: courseRun.course.name, label: courseRun.information }) }}
          </h2>
          <div v-if="!courseRun.isNewRecord" class="mt-1">
            <p class="text-gray-700 text-sm md:text-base dark:text-gray-200">
              <span class="text-gray-700 dark:text-gray-200">{{ courseRun.infoLabel }}</span>
            </p>
          </div>
        </div>
      </div>
    </template>

    <div v-if="courseRun" class="h-full w-full">
      <o-scroll class="h-full py-8">
        <div class="h-full flex flex-col">
          <div class="pb-8 py-8 px-6 flex-shrink-0">
            <o-field-group :label="$t('general.label')" :info="$t('pages.course.labelForCourseRunInfo')" class="mb-4 block">
              <text-field
                v-model="courseRun.information"
                :label="$t('general.label')"
                :leading="courseRun.course.name"
                required
              />
            </o-field-group>

            <div class="mb-5">
              <pricing-schemes :payment-config="courseRun.paymentConfig" />
            </div>

            <!--<o-field-group
              label="Rescheduling options"
              info="If a customer misses a session, what options do they have to reschedule"
              class="mb-4 block"
            >
              <segment-field
                v-model="courseRun.makeupConfig.mode"
                :options="[
                  { value: CourseRunMakeupMode.none, label: 'Not allowed' },
                  { value: CourseRunMakeupMode.any_service, label: 'Rebook any service' },
                  { value: CourseRunMakeupMode.any_run, label: 'Rebook any run' }
                ]"
                required
              />
            </o-field-group>

            <o-field-group
              label="Allow enrolling after start"
              info="Can customers book this course run after it has started"
              class="mb-4 block"
            >
              <segment-field
                v-model="courseRun.partialConfig.mode"
                :options="[
                  { value: CourseRunPartialMode.none, label: 'No' },
                  { value: CourseRunPartialMode.late, label: 'Yes' },
                ]"
                required
              />
            </o-field-group>-->

            <o-field-group :label="$t('pages.course.edit.enabled')" :info="$t('pages.course.edit.enabledInfo')" col reverse class="mb-4 block">
              <toggle-field
                v-model="courseRun.enabled"
              />
            </o-field-group>

            <o-field-group :label="$t('pages.course.edit.allowInternet')" :info="$t('pages.course.edit.allowInternetInfo')" col reverse class="mb-4 block">
              <toggle-field
                v-model="courseRun.allowInternet"
              />
            </o-field-group>

            <o-field-group
              v-if="!courseRun.isNewRecord"
              :label="$t('pages.course.edit.allowMakeups')"
              :info="$t('pages.course.edit.allowMakeupsInfo')"
              col
              reverse
              class="mb-4 block"
            >
              <toggle-field v-model="courseRun.makeupConfig.enabled" />
            </o-field-group>

            <o-field-group :label="$t('pages.course.edit.courseCapacity')" :info="$t('pages.course.edit.courseCapacityInfo')">
              <div v-if="courseRun.customerCounts.lowestSpace === 0">
                <OAlert show variant="danger" :title="$t('pages.course.edit.courseHasNoCapacity')" :content="$t('pages.course.edit.courseHasNoCapacityInfo')" class="text-sm" />
              </div>

              <div v-else class="mt-3">
                <div class="w-full bg-gray-100 dark:bg-gray-800 rounded-full h-3 relative">
                  <div
                    class="rounded-full h-3 absolute"
                    :class="[
                      { 'bg-green-500': courseRun.customerCounts.customersCount / courseRun.customerCounts.lowestCustomerMax * 100 < 50 },
                      { 'bg-yellow-500': courseRun.customerCounts.customersCount / courseRun.customerCounts.lowestCustomerMax * 100 >= 50 && courseRun.customerCounts.customersCount / courseRun.customerCounts.lowestCustomerMax * 100 < 80 },
                      { 'bg-red-500': courseRun.customerCounts.customersCount / courseRun.customerCounts.lowestCustomerMax * 100 >= 80 }
                    ]"
                    :style="{ width: courseRun.customerCounts.customersCount / courseRun.customerCounts.lowestCustomerMax * 100 + '%' }"
                  />
                </div>
                <div class="text-sm leading-5 mt-2">
                  <div class="flex items-center justify-between">
                    <span class="opacity-70">
                      {{ courseRun.customerCounts.customersCount }} {{ courseRun.customerCounts.customersCount === 1 ? $t('pages.course.edit.customerSingular') : $t('pages.course.edit.customerPlural') }}
                    </span>
                    <o-chip v-if="courseRun.customerCounts.lowestSpace === 0" variant="danger" size="sm">
                      {{ $t('general.full') }}
                    </o-chip>
                    <span v-else-if="courseRun.customerCounts.lowestSpace > 0" class="opacity-70">
                      {{ $t('components.reservation.edit.info.spotsLeft', { count: courseRun.customerCounts.lowestSpace }) }}
                    </span>
                  </div>
                </div>
              </div>
            </o-field-group>
          </div>

          <div class="w-full flex-1 min-h-xs">
            <o-tabs ref="tabs" nav-class="px-5" container-class="py-5 bg-gray-50 h-full dark:bg-opacity-0" scroll>
              <o-tab :name="$t('pages.course.edit.tabSessions')" icon="calendar" :alert="courseRun.reservations?.length">
                <div v-if="courseRun.reservations" class="px-4">
                  <div v-if="!courseRun.reservations.length" class="flex flex-col justify-center items-center">
                    <p class="text-sm text-center mb-3">
                      {{ $t('pages.course.edit.sessionsEmptyHelp') }}
                    </p>

                    <o-dropdown>
                      <o-button variant="primary">
                        {{ $t('pages.course.edit.addSession') }}
                      </o-button>

                      <template #content>
                        <o-dropdown-item
                          tag-name="o-panel-link"
                          path="/appointment/create?type=group"
                          :props="{
                            course: courseRun.course,
                            courseRun,
                            callback: handleAddNewSession
                          }"
                        >
                          {{ $t('pages.course.edit.createNewSession') }}
                        </o-dropdown-item>

                        <o-dropdown-item
                          @click="handleAddSessionDialog"
                        >
                          {{ $t('pages.course.edit.addExistingSession') }}
                        </o-dropdown-item>
                      </template>
                    </o-dropdown>
                  </div>

                  <template v-if="courseRun.reservations.length">
                    <div class="flex items-center justify-between mb-3">
                      <div>
                        <p class="text-gray-700 dark:text-gray-200">
                          <span class="font-semibold">{{ $t('pages.course.edit.sessionsCount', { n: courseRun.reservations.length }, courseRun.reservations.length) }}</span> <span v-if="sessionsDateRange">({{ sessionsDateRange }})</span>
                        </p>
                      </div>
                      <o-dropdown>
                        <o-button variant="primary">
                          {{ $t('pages.course.edit.addSession') }}
                        </o-button>

                        <template #content>
                          <o-dropdown-item
                            tag-name="o-panel-link"
                            path="/appointment/create?type=group"
                            :props="{
                              course: courseRun.course,
                              courseRun,
                              callback: handleAddNewSession
                            }"
                          >
                            {{ $t('pages.course.edit.createNewSession') }}
                          </o-dropdown-item>

                          <o-dropdown-item
                            @click="handleAddSessionDialog"
                          >
                            {{ $t('pages.course.edit.addExistingSession') }}
                          </o-dropdown-item>
                        </template>
                      </o-dropdown>
                    </div>

                    <div>
                      <Session
                        v-for="(item, key) in courseRun.reservations"
                        :key="key"
                        :reservation="item.reservation"
                        :removable="true"
                        class="mb-2"
                        @remove="handleRemoveSession"
                      />
                    </div>
                  </template>
                </div>
              </o-tab>
              <o-tab v-if="!courseRun.isNewRecord" :name="$t('pages.course.edit.tabCustomers')" icon="customer" :alert="activeCustomers.length">
                <course-customers
                  v-if="customers"
                  :course-run="courseRun"
                  :customers="customers"
                  mode="edit"
                />
              </o-tab>
              <o-tab v-if="!courseRun.isNewRecord && courseRun.makeupConfig.enabled" id="makeup-config" :name="$t('pages.course.edit.tabMakeupConfig')" icon="reschedule">
                <div class="px-4 pb-6">
                  <o-field-group
                    :label="$t('pages.course.edit.allowMakeupForNoShows')"
                    :info="$t('pages.course.edit.allowMakeupForNoShowsInfo')"
                    col
                    reverse
                    class="mb-4 block"
                  >
                    <toggle-field v-model="courseRun.makeupConfig.allowedForNoShows" />
                  </o-field-group>

                  <o-field-group
                    :label="$t('pages.course.edit.allowMakeupForCancellations')"
                    :info="$t('pages.course.edit.allowMakeupForCancellationsInfo')"
                    col
                    reverse
                    class="mb-4 block"
                  >
                    <toggle-field v-model="courseRun.makeupConfig.allowedForCancellations" />
                  </o-field-group>

                    <o-field-group
                      :label="$t('pages.course.edit.makeupCourseRuns')"
                      :info="$t('pages.course.edit.makeupCourseRunsInfo')"
                      class="mb-4 block"
                    >
                      <div class="border rounded-lg dark:border-gray-800">
                        <div v-if="courseRun.makeupConfig.courseRuns?.length">
                          <div
                            v-for="run in courseRun.makeupConfig.courseRuns"
                            :key="run.idCourseRun"
                            class="flex items-center justify-between p-3 border-b dark:border-gray-800 last:border-none"
                          >
                            <div>
                              <p class="text-sm font-medium">
                                {{ run.course?.name }} - {{ run.information }}
                              </p>
                              <p v-if="run.dates?.[0]" class="text-xs text-gray-500">
                                {{ $date.convert(run.dates[0], undefined, $date.presets.long) }}
                              </p>
                            </div>
                            <o-button size="sm" flat icon="x" @click="handleRemoveMakeupCourseRun(run)" />
                          </div>
                        </div>
                        <div v-else class="p-3 text-sm text-gray-500 text-center">
                          {{ $t('pages.course.edit.noCourseRunsSelected') }}
                        </div>
                        <div class="p-2 border-t dark:border-gray-800 bg-gray-50 dark:bg-gray-900">
                          <o-button size="sm" flat icon="add" @click="handleAddMakeupCourseRuns">
                            {{ $t('pages.course.edit.addCourseRuns') }}
                          </o-button>
                        </div>
                      </div>
                    </o-field-group>

                    <o-field-group
                      :label="$t('pages.course.edit.makeupReservations')"
                      :info="$t('pages.course.edit.makeupReservationsInfo')"
                      class="block"
                    >
                      <div class="border rounded-lg dark:border-gray-800">
                        <div v-if="courseRun.makeupConfig.reservations?.length">
                          <div
                            v-for="reservation in courseRun.makeupConfig.reservations"
                            :key="reservation.idReservation"
                            class="flex items-center justify-between p-3 border-b dark:border-gray-800 last:border-none"
                          >
                            <div>
                              <p class="text-sm font-medium">
                                {{ reservation.infoLabel || reservation._servicesLabel }}
                              </p>
                              <p class="text-xs text-gray-500">
                                {{ reservation.dateLabel }}
                              </p>
                            </div>
                            <o-button size="sm" flat icon="x" @click="handleRemoveMakeupReservation(reservation)" />
                          </div>
                        </div>
                        <div v-else class="p-3 text-sm text-gray-500 text-center">
                          {{ $t('pages.course.edit.noReservationsSelected') }}
                        </div>
                        <div class="p-2 border-t dark:border-gray-800 bg-gray-50 dark:bg-gray-900">
                          <o-button size="sm" flat icon="add" @click="handleAddMakeupReservations">
                            {{ $t('pages.course.edit.addReservations') }}
                          </o-button>
                        </div>
                      </div>
                    </o-field-group>
                </div>
              </o-tab>
            </o-tabs>
          </div>
        </div>
      </o-scroll>
    </div>

    <template #footer>
      <div v-if="courseRun">
        <div class="flex justify-between align-items">
          <div class="flex items-center space-x-1">
            <o-button outline flat class="mr-2" @click="cancel()">
              {{ $t('general.cancel') }}
            </o-button>
          </div>
          <div class="flex items-center space-x-1">
            <o-button
              variant="primary"
              outline
              icon="save"
              class="mr-2"
              @click="save()"
            >
              {{ $t('general.save') }}
            </o-button>
            <o-button
              variant="primary"
              icon="save"
              class="mr-2"
              @click="save(true)"
            >
              {{ $t('general.saveAndClose') }}
            </o-button>
          </div>
        </div>
      </div>
    </template>

    <template v-if="courseRun">
      <deleteDialog ref="deleteDialog" @success="fetchCourseRun" />
      <selectAvailableReservationsDialog ref="addSessionDialog" mode="sessions" :portal="panel.portal" @update="handleAddExistingSessions" />
      <selectAvailableReservationsDialog ref="addMakeupReservationsDialog" mode="makeups" :portal="panel.portal" @update="handleUpdateMakeupReservations" />
      <addMakeupCourseRunsDialog ref="addMakeupCourseRunsDialog" @update="handleUpdateMakeupCourseRuns" />
    </template>

    <o-dialog
      ref="confirmDialog"
      :title="$t('general.unsavedChanges')"
      :info="$t('general.unsavedChangesInfo')"
      icon="courseRun"
      :options="[
        { text: $t('general.back'), type: 'cancel' },
        {
          text: $t('general.closeWithoutSaving'),
          type: 'confirm',
          variant: undefined,
          flat: true,
          outline: true,
          callback: () => cancel(true)
        },
        {
          text: $t('general.saveAndClose'),
          type: 'confirm',
          response: 'save',
          callback: () => save(true)
        }
      ]"
      :portal="panel.portal"
    />

    <o-dialog
      ref="removeSessionDialog"
      :title="$t('pages.course.edit.removeSessionTitle')"
      :info="$t('pages.course.edit.removeSessionInfo')"
      icon="delete"
      :options="[
        { text: $t('general.cancel'), type: 'cancel' },
        {
          text: $t('pages.course.edit.removeSessionRemoveOnly'),
          type: 'confirm',
          variant: 'primary',
          callback: () => confirmRemoveSession('none')
        },
        {
          text: $t('pages.course.edit.removeSessionCancelClass'),
          type: 'confirm',
          variant: 'warning',
          callback: () => confirmRemoveSession('cancel')
        },
        {
          text: $t('pages.course.edit.removeSessionDeleteClass'),
          type: 'confirm',
          variant: 'danger',
          callback: () => confirmRemoveSession('delete')
        }
      ]"
      :portal="panel.portal"
    />
  </panel-layout>
</template>
<script setup lang="ts">


// @ts-ignore
import DeleteDialog from '@/components/courseRun/actions/Delete.vue'
// @ts-ignore
import AddSessionDialog from '@/components/courseRun/actions/AddSession.vue'
// @ts-ignore
import SelectAvailableReservationsDialog from '@/components/courseRun/actions/SelectAvailableReservations.vue'
// @ts-ignore
import AddMakeupCourseRunsDialog from '@/components/courseRun/actions/AddMakeupCourseRuns.vue'

// @ts-ignore
import Session from '@/components/courseRun/elements/Session.vue'

// @ts-ignore
import CourseCustomers from '@/components/courseRun/elements/CourseCustomers.vue'
// @ts-ignore
import PricingSchemes from '@/components/courseRun/elements/PricingSchemes.vue'
// @ts-ignore
import PanelLayout from '@/components/panel/PanelLayout.vue'

import { OTabs, OTab, OAlert, OAvatar, ODropdown, ODropdownItem } from '@/components/elements'
import { TextField, TextAreaField, DateField, CurrencyField, ToggleField, NumberField, SegmentField } from '@/components/fields'

import { usePanel } from '~/composables/usePanel'
import { CustomerCourseStatus } from '@/_api/models/course'

const panel = usePanel()

const props = defineProps<{ idCourseRun?: string; idCourse?: string }>()

const { $api, $date, $notification, $bus } = useNuxtApp()
const router = useRouter()
const instance = getCurrentInstance()

const tabs = ref<any>(null)
const courseRun = ref<any>(null)
const customers = ref<any>(null)
const errors = ref(false)
const loading = ref(true)
const newInstalment = ref({ amount: 0.5, offset: 30 })

const confirmDialog = ref<any>()
const deleteDialog = ref<any>()
const addSessionDialog = ref<any>()
const addMakeupReservationsDialog = ref<any>()
const addMakeupCourseRunsDialog = ref<any>()
const removeSessionDialog = ref<any>()
const sessionToRemove = ref<any>(null)

const { unsavedChanges, snapshot } = useUnsavedChanges(courseRun)

const activeCustomers = computed(() => customers.value?.items?.filter((customer: any) => customer.status !== CustomerCourseStatus.cancelled) || [])

const sessionsDateRange = computed(() => {
  if (!courseRun.value?.reservations?.length) {
    return null
  }

  const reservations = courseRun.value.reservations
    .map((item: any) => item.reservation)
    .filter((reservation: any) => reservation?.date?.date)
    .sort((a: any, b: any) => a.date.date - b.date.date)

  if (!reservations.length) {
    return null
  }

  const first = $date.format(reservations[0].date.date, 'd MMM')
  const last = $date.format(reservations[reservations.length - 1].date.date, 'd MMM')

  if (first === last) {
    return first
  }

  return `${first} - ${last}`
})

const fetchCourseRun = async (idCourseRun?: string) => {
  try {
    if (!idCourseRun) {
      const response = await $api.course.getRunDefaults()
      courseRun.value = response.data
      courseRun.value.reservations = []
    } else {
      const response = await $api.course.getRunById(idCourseRun)
      courseRun.value = response.data
      courseRun.value.paymentConfig.instalments = courseRun.value.paymentConfig.instalments || []
    }
    snapshot()
  } catch (e) {
    console.log(e)
  }
}

const fetchCustomers = async () => {
  try {
    const response = await $api.course.getRunCustomers(courseRun.value)
    customers.value = response
  } catch (e) {
    console.log(e)
  }
}

const save = async (close = false) => {
  loading.value = true
  errors.value = false

  try {
    const isNewRecord = courseRun.value.isNewRecord
    const data = { ...courseRun.value }
    delete data.reservations
    const response = await $api.course.saveRun(data)
    const result = response.data

    if (result) {
      courseRun.value = result
      snapshot()

      $notification.toast({
        title: 'CourseRun details saved',
        text: 'The courseRun details were successfully saved',
        variant: 'success'
      })

      $bus.emit.updated('CourseRun', result)

      if (close) {
        panel.close()
      } else if (isNewRecord) {
        // Swap in-place to the freshly-created CourseRun's edit view.
        panel.replace('course/edit', { idCourseRun: String(result.idCourseRun) })
      }
    }
  } catch (e: any) {
    console.log(e)
    $notification.error({ errors: e?.errors })
    loading.value = false
    return false
  }

  loading.value = false
}

const cancel = (force = false) => {
  if (!force && unsavedChanges.value) {
    confirmDialog.value.open()
  } else {
    panel.close(force)
  }
}

const handleDelete = () => {
  deleteDialog.value.open(courseRun.value)
}

const handleEnableEarlyBird = () => {
  const price = courseRun.value.paymentConfig.price
  courseRun.value.paymentConfig.earlyOffset = 10
  courseRun.value.paymentConfig.earlyPrice = price ? price - (price * 0.2) : 0
}

const handleDisableEarlyBird = () => {
  courseRun.value.paymentConfig.earlyOffset = null
  courseRun.value.paymentConfig.earlyPrice = null
}

const handleBreakAddClick = () => {
  courseRun.value.paymentConfig.instalments.push({ ...newInstalment.value })
  newInstalment.value = { amount: 0.5, offset: 30 }
}

const handleAddInstalment = () => {
  courseRun.value.paymentConfig.instalments.push({ ...newInstalment.value })
  newInstalment.value = { amount: 0.5, offset: 30 }
}

const handleRemoveInstalment = (index: number) => {
  courseRun.value.paymentConfig.instalments.splice(index, 1)
}

const handleAddSessionDialog = () => {
  const existingReservations = courseRun.value.reservations?.map((item: any) => item.reservation) || []
  addSessionDialog.value.open(courseRun.value, existingReservations)
}

const handleAddNewSession = async (reservation: any) => {
  try {
    const reservations = [reservation]

    if (reservation?.recurrence?.ids) {
      for (const id of reservation.recurrence.ids) {
        reservations.push({ idReservation: id })
      }
    }
    const response = await $api.course.addReservations(courseRun.value, reservations)
    const result = response.data

    if (result) {
      courseRun.value = result
      snapshot()

      $notification.toast({
        title: 'Session added',
        text: 'The session was successfully added',
        variant: 'success'
      })
    }
  } catch (e) {
    console.log(e)
  }
}

const handleAddExistingSessions = async (reservations: any) => {
  try {
    const response = await $api.course.addReservations(courseRun.value, reservations)
    const result = response.data

    if (result) {
      courseRun.value = result
      snapshot()

      $notification.toast({
        title: 'Session added',
        text: 'The session was successfully added',
        variant: 'success'
      })
    }
  } catch (e) {
    console.log(e)
  }
}

const handleRemoveSession = (reservation: any) => {
  sessionToRemove.value = reservation
  removeSessionDialog.value.open()
}

const confirmRemoveSession = async (action = 'none') => {
  if (!sessionToRemove.value) {
    return
  }

  loading.value = true

  try {
    const reservations = [{ idReservation: sessionToRemove.value.idReservation }]
    const response = await $api.course.removeReservations(courseRun.value, reservations, action)
    const result = response.data

    if (result) {
      courseRun.value = result
      snapshot()

      $notification.toast({
        title: 'Session removed',
        text: 'The session was successfully removed',
        variant: 'success',
      })

      $bus.emit.updated('CourseRun', result)
    }
  } catch (e: any) {
    console.error(e)
    $notification.error({ errors: e?.errors })
  } finally {
    sessionToRemove.value = null
    loading.value = false
    removeSessionDialog.value.close()
  }
}

const handleAddMakeupReservations = () => {
  addMakeupReservationsDialog.value.open(courseRun.value, courseRun.value.makeupConfig.reservations || [])
}

const handleUpdateMakeupReservations = (reservations: any[]) => {
  courseRun.value.makeupConfig.reservations = [
    ...(courseRun.value.makeupConfig.reservations || []),
    ...reservations,
  ]
}

const handleRemoveMakeupReservation = (reservation: any) => {
  courseRun.value.makeupConfig.reservations = courseRun.value.makeupConfig.reservations.filter(
    (item: any) => item.idReservation !== reservation.idReservation,
  )
}

const handleAddMakeupCourseRuns = () => {
  addMakeupCourseRunsDialog.value.open(courseRun.value, courseRun.value.makeupConfig.courseRuns || [])
}

const handleUpdateMakeupCourseRuns = (courseRuns: any[]) => {
  courseRun.value.makeupConfig.courseRuns = [
    ...(courseRun.value.makeupConfig.courseRuns || []),
    ...courseRuns,
  ]
}

const handleRemoveMakeupCourseRun = (run: any) => {
  courseRun.value.makeupConfig.courseRuns = courseRun.value.makeupConfig.courseRuns.filter(
    (item: any) => item.idCourseRun !== run.idCourseRun,
  )
}

watch(() => props.idCourseRun, async (idCourseRun) => {
  loading.value = true
  await fetchCourseRun(idCourseRun)
  fetchCustomers()
  loading.value = false
}, { immediate: true })

// When make-ups are enabled, jump straight to the make-up config tab so it can be set up.
watch(() => courseRun.value?.makeupConfig?.enabled, (enabled, wasEnabled) => {
  if (enabled && !wasEnabled) {
    nextTick(() => {
      tabs.value?.selectTab('#makeup-config')
    })
  }
})

onMounted(() => {
  $bus.on.all('CustomerCourse', instance?.uid, () => {
    fetchCustomers()
  })
})

onBeforeUnmount(() => {
  $bus.off.all(instance?.uid)
})
</script>
